import React, { useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useForm } from 'react-final-form';

// Import config and utils
import { useIntl } from '../../util/reactIntl';
import { FieldArray } from 'react-final-form-arrays';

import {
  SCHEMA_TYPE_ENUM,
  SCHEMA_TYPE_MULTI_ENUM,
  SCHEMA_TYPE_TEXT,
  SCHEMA_TYPE_TEXTAREA,
  SCHEMA_TYPE_LONG,
  SCHEMA_TYPE_BOOLEAN,
  SCHEMA_TYPE_YOUTUBE,
  SCHEMA_TYPE_PRICE,
  SCHEMA_TYPE_REPEATER,
  SCHEMA_TYPE_OPEN_AND_CLOSE_TIME,
  SCHEMA_TYPE_NUMBER
} from '../../util/types';
import {
  required,
  nonEmptyArray,
  validateInteger,
  validateYoutubeURL,
} from '../../util/validators';
import appSettings from '../../config/settings';
// Import shared components
import { FieldCheckboxGroup, FieldSelect, FieldTextInput, FieldBoolean, FieldCurrencyInput, IconClose } from '../../components';
// Import modules from this directory
import css from './CustomExtendedDataField.module.css';
import classNames from 'classnames';

const createFilterOptions = options => options.map(o => {
  return {
    key: o.option,
    label: o.label,
    value: o.option,
  }
});

const getLabel = fieldConfig => fieldConfig?.saveConfig?.label || fieldConfig?.label;

const CustomFieldEnum = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
  const { enumOptions = [], saveConfig } = fieldConfig || {};
  const { placeholderMessage, isRequired, requiredMessage } = saveConfig || {};
  const validateMaybe = isRequired
    ? { validate: required(requiredMessage || defaultRequiredMessage) }
    : {};
  const placeholder =
    placeholderMessage ||
    intl.formatMessage({ id: 'CustomExtendedDataField.placeholderSingleSelect' });
  const filterOptions = createFilterOptions(enumOptions);

  const label = getLabel(fieldConfig);
  // If there are no options, we don't want to render the select field
  return filterOptions ? (
    <FieldSelect
      className={css.customField}
      name={name}
      id={formId ? `${formId}.${name}` : name}
      label={label}
      {...validateMaybe}
    >
      <option disabled hidden value="">
        {placeholder}
      </option>
      {filterOptions.map(optionConfig => {
        const key = optionConfig.key;
        return (
          <option key={key} value={key}>
            {optionConfig.label}
          </option>
        );
      })}
    </FieldSelect>
  ) : null;
};

const CustomFieldMultiEnum = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId } = props;
  const { enumOptions = [], saveConfig } = fieldConfig || {};
  const { isRequired, requiredMessage } = saveConfig || {};
  const label = getLabel(fieldConfig);
  const validateMaybe = isRequired
    ? { validate: nonEmptyArray(requiredMessage || defaultRequiredMessage) }
    : {};
  return enumOptions ? (
    <FieldCheckboxGroup
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      label={label}
      options={createFilterOptions(enumOptions)}
      {...validateMaybe}
    />
  ) : null;
};

const CustomFieldText = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
  const { placeholderMessage, isRequired, requiredMessage } = fieldConfig?.saveConfig || {};
  const label = getLabel(fieldConfig);
  const validateMaybe = isRequired
    ? { validate: required(requiredMessage || defaultRequiredMessage) }
    : {};
  const placeholder =
    placeholderMessage || intl.formatMessage({ id: 'CustomExtendedDataField.placeholderText' });

  return (
    <FieldTextInput
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      type="text"
      label={label}
      placeholder={placeholder}
      {...validateMaybe}
    />
  );
};

const CustomFieldLong = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
  const { minimum, maximum, saveConfig } = fieldConfig;
  const { placeholderMessage, isRequired, requiredMessage } = saveConfig || {};
  const label = getLabel(fieldConfig);
  const placeholder =
    placeholderMessage || intl.formatMessage({ id: 'CustomExtendedDataField.placeholderLong' });
  const numberTooSmallMessage = intl.formatMessage(
    { id: 'CustomExtendedDataField.numberTooSmall' },
    { min: minimum }
  );
  const numberTooBigMessage = intl.formatMessage(
    { id: 'CustomExtendedDataField.numberTooBig' },
    { max: maximum }
  );

  // Field with schema type 'long' will always be validated against min & max
  const validate = (value, min, max) => {
    const requiredMsg = requiredMessage || defaultRequiredMessage;
    return isRequired && value == null
      ? requiredMsg
      : validateInteger(value, max, min, numberTooSmallMessage, numberTooBigMessage);
  };

  return (
    <FieldTextInput
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      type="number"
      step="1"
      parse={value => {
        const parsed = Number.parseInt(value, 10);
        return Number.isNaN(parsed) ? null : parsed;
      }}
      label={label}
      placeholder={placeholder}
      validate={value => validate(value, minimum, maximum)}
      onWheel={e => {
        // fix: number input should not change value on scroll
        if (e.target === document.activeElement) {
          // Prevent the input value change, because we prefer page scrolling
          e.target.blur();

          // Refocus immediately, on the next tick (after the current function is done)
          setTimeout(() => {
            e.target.focus();
          }, 0);
        }
      }}
    />
  );
};

const CustomFieldBoolean = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl, isCheckbox } = props;
  const { placeholderMessage, isRequired, requiredMessage } = fieldConfig?.saveConfig || {};
  const label = getLabel(fieldConfig);
  const validateMaybe = isRequired
    ? { validate: required(requiredMessage || defaultRequiredMessage) }
    : {};
  const placeholder =
    placeholderMessage || intl.formatMessage({ id: 'CustomExtendedDataField.placeholderBoolean' });

  return (
    <FieldBoolean
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      label={label}
      placeholder={placeholder}
      isCheckbox={isCheckbox}
      {...validateMaybe}
    />
  );
};

const CustomFieldYoutube = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
  const { placeholderMessage, isRequired, requiredMessage } = fieldConfig?.saveConfig || {};
  const label = getLabel(fieldConfig);
  const placeholder =
    placeholderMessage ||
    intl.formatMessage({ id: 'CustomExtendedDataField.placeholderYoutubeVideoURL' });

  const notValidUrlMessage = intl.formatMessage({
    id: 'CustomExtendedDataField.notValidYoutubeVideoURL',
  });

  const validate = value => {
    const requiredMsg = requiredMessage || defaultRequiredMessage;
    return isRequired && value == null
      ? requiredMsg
      : validateYoutubeURL(value, notValidUrlMessage);
  };

  return (
    <FieldTextInput
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      type="text"
      label={label}
      placeholder={placeholder}
      validate={value => validate(value)}
    />
  );
};

const CustomPricingField = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
  const { minimum, maximum, saveConfig } = fieldConfig;
  const currencyConfig = appSettings.getCurrencyFormatting('USD');
  const { placeholderMessage, isRequired, requiredMessage } = saveConfig || {};
  const label = getLabel(fieldConfig);
  const placeholder =
    placeholderMessage || intl.formatMessage({ id: 'CustomExtendedDataField.placeholderPrice' });
  const numberTooSmallMessage = intl.formatMessage(
    { id: 'CustomExtendedDataField.numberTooSmall' },
    { min: minimum }
  );
  const numberTooBigMessage = intl.formatMessage(
    { id: 'CustomExtendedDataField.numberTooBig' },
    { max: maximum }
  );

  // Field with schema type 'long' will always be validated against min & max
  const validateMaybe = isRequired
    ? { validate: required(requiredMessage || defaultRequiredMessage) }
    : {};

  return (
    <FieldCurrencyInput
      className={css.customField}
      id={formId ? `${formId}.${name}` : name}
      name={name}
      label={label}
      placeholder={placeholder}
      {...validateMaybe}
      currencyConfig={currencyConfig}
    />
  );
}

const CustomFieldRepeater = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl, values } = props;
  const form = useForm();
  const { minimum, maximum, saveConfig, sub_fields } = fieldConfig;
  const { placeholderMessage, isRequired, requiredMessage } = saveConfig || {};
  const label = getLabel(fieldConfig);

  // Add validation for required repeater
  const validateRepeater = value => {
    if (isRequired && (!value || value.length === 0)) {
      return requiredMessage || defaultRequiredMessage || 'This field is required';
    }
    return undefined;
  };

  // Ensure all repeater items have IDs
  useEffect(() => {
    if (values[name] && Array.isArray(values[name])) {
      const hasItemsWithoutId = values[name].some(item => !item.id);
      if (hasItemsWithoutId) {
        // Create a new array with IDs for items that don't have them
        const updatedItems = values[name].map(item => ({
          ...item,
          id: item.id || uuidv4()
        }));
        // Update the form values using the form context
        form.change(name, updatedItems);
      }
    }
  }, [name, values[name], form]);

  return (
    <FieldArray name={name} validate={validateRepeater}>
      {({ fields }) => (
        <div className={css.repeaterField}>
          <label className={css.repeaterLabel}>
            {label}
            {isRequired && <span className={css.required}> *</span>}
          </label>
          {fields.map((fieldName, index) => {
            const id = values[name]?.[index]?.id || uuidv4();
            return (
              <div key={fieldName} className={css.repeaterItem}>
                <FieldTextInput 
                  name={`${fieldName}.id`} 
                  type="hidden" 
                  defaultValue={id} 
                />
                {sub_fields.map(subField => {
                  if (!subField.dependance) {
                    return (
                      <div key={subField.key} className={classNames(css.subField, {
                        [css.fullWidth]: subField.fullWidth
                      })}>
                        <CustomExtendedDataField
                          name={`${fieldName}.${subField.key}`}
                          fieldConfig={{
                            key: subField.key,
                            schemaType: subField.type,
                            enumOptions: subField.options || [],
                            scope: 'public',
                            saveConfig: {
                              label: subField.label,
                              placeholderMessage: subField.label,
                              isRequired: subField.required,
                            },
                            showConfig: {
                              label: subField.label,
                              isDetail: true,
                            },
                          }}
                          defaultRequiredMessage={defaultRequiredMessage}
                          formId={formId}
                          isCheckbox={subField.isCheckbox}
                        />
                      </div>
                    )
                  }
                  // Convertimos los valores a booleanos para comparación
                  const dependanceValue = subField.dependanceValue === 'true' || subField.dependanceValue === true;
                  const currentValue = values[name][index][subField.dependance] === 'true' || values[name][index][subField.dependance] === true;
                  // Si tiene dependanceValue, validamos que coincida con el valor en values
                  if (subField.dependanceValue !== undefined) {
                    if ((values[subField.dependance] !== null && values[subField.dependance] !== undefined) && currentValue == dependanceValue) {
                      return (
                        <div key={subField.key} className={classNames(css.subField, {
                            [css.fullWidth]: subField.fullWidth
                        })}>    
                          <CustomExtendedDataField
                            name={`${fieldName}.${subField.key}`}
                            fieldConfig={{
                              key: subField.key,
                              schemaType: subField.type,
                              enumOptions: subField.options || [],
                              scope: 'public',
                              saveConfig: {
                                label: subField.label,
                                placeholderMessage: subField.label,
                                isRequired: subField.required,
                              },
                              showConfig: {
                                label: subField.label,
                                isDetail: true,
                              },
                            }}
                            isCheckbox={subField.isCheckbox}
                            defaultRequiredMessage={defaultRequiredMessage}
                            formId={formId}
                          />
                        </div>
                      )
                    }
                  }
                  if (!values[name][index][subField.dependance]) {
                    return null;
                  }

                  return (
                    <div key={subField.key} className={classNames(css.subField, {
                      [css.fullWidth]: subField.fullWidth
                    })}>
                      <CustomExtendedDataField
                        name={`${fieldName}.${subField.key}`}
                        fieldConfig={{
                          key: subField.key,
                          schemaType: subField.type,
                          enumOptions: subField.options || [],
                          scope: 'public',
                          saveConfig: {
                            label: subField.label,
                            placeholderMessage: subField.placeholder || subField.label,
                            isRequired: subField.required,
                          },
                          showConfig: {
                            label: subField.label,
                            isDetail: true,
                          },
                        }}
                        defaultRequiredMessage={defaultRequiredMessage}
                        formId={formId}
                        isCheckbox={subField.isCheckbox}
                      />
                    </div>
                  )
                })}
                <button
                  type="button"
                  className={css.removeButton}
                  onClick={() => fields.remove(index)}
                >
                  <IconClose className={css.removeIcon} />
                </button>
              </div>
            )})}
          <button
            type="button"
            className={css.addButton}
            onClick={() => fields.push({ id: uuidv4() })}
          >
            Add {label?.toLowerCase()}
          </button>
        </div>
      )}
    </FieldArray>
  )
}

const CustomFieldOpenAndCloseTime = props => {
  const { name, fieldConfig, defaultRequiredMessage, formId, intl } = props;
  const { minimum, maximum, saveConfig } = fieldConfig;
  const { placeholderMessage, isRequired, requiredMessage } = saveConfig || {};
  const label = getLabel(fieldConfig);
  const placeholder =
    placeholderMessage || intl.formatMessage({ id: 'CustomExtendedDataField.placeholderPrice' });
  const numberTooSmallMessage = intl.formatMessage(
    { id: 'CustomExtendedDataField.numberTooSmall' },
    { min: minimum }
  );
  const numberTooBigMessage = intl.formatMessage(
    { id: 'CustomExtendedDataField.numberTooBig' },
    { max: maximum }
  );

  // Field with schema type 'long' will always be validated against min & max
  const validateMaybe = isRequired
    ? { validate: required(requiredMessage || defaultRequiredMessage) }
    : {};

  return (
    <div className={css.row}>
      <FieldTextInput
        className={css.customField}
        id={formId ? `${formId}.${name}StartTime` : name + "StartTime"}
        name={name + "StartTime"}
        type="time"
        label={label + " start time"}
        placeholder={placeholder}
        {...validateMaybe}
        onWheel={e => {
          // fix: number input should not change value on scroll
          if (e.target === document.activeElement) {
            // Prevent the input value change, because we prefer page scrolling
            e.target.blur();

            // Refocus immediately, on the next tick (after the current function is done)
            setTimeout(() => {
              e.target.focus();
            }, 0);
          }
        }}
      />
      <FieldTextInput
        className={css.customField}
        id={formId ? `${formId}.${name}EndTime` : name + "EndTime"}
        name={name + "EndTime"}
        type="time"
        label={label + " end time"}
        placeholder={placeholder}
        {...validateMaybe}
        onWheel={e => {
          // fix: number input should not change value on scroll
          if (e.target === document.activeElement) {
            // Prevent the input value change, because we prefer page scrolling
            e.target.blur();

            // Refocus immediately, on the next tick (after the current function is done)
            setTimeout(() => {
              e.target.focus();
            }, 0);
          }
        }}
      />
    </div>
  );
}

/**
 * Return Final Form field for each configuration according to schema type.
 *
 * These custom extended data fields are for generating input fields from configuration defined
 * in marketplace-custom-config.js. Other panels in EditListingWizard might add more extended data
 * fields (e.g. shipping fee), but these are independently customizable.
 *
 * @param {Object} props should contain fieldConfig that defines schemaType, enumOptions?, and
 * saveConfig for the field.
 */
const CustomExtendedDataField = props => {
  const intl = useIntl();
  const { enumOptions = [], schemaType } = props?.fieldConfig || {};
  const renderFieldComponent = (FieldComponent, props) => <FieldComponent {...props} intl={intl} />;
  return schemaType === SCHEMA_TYPE_ENUM && enumOptions
    ? renderFieldComponent(CustomFieldEnum, props)
    : schemaType === SCHEMA_TYPE_MULTI_ENUM && enumOptions
      ? renderFieldComponent(CustomFieldMultiEnum, props)
      : schemaType === SCHEMA_TYPE_TEXT || schemaType === SCHEMA_TYPE_TEXTAREA
        ? renderFieldComponent(CustomFieldText, props)
        : schemaType === SCHEMA_TYPE_LONG || schemaType === SCHEMA_TYPE_NUMBER
          ? renderFieldComponent(CustomFieldLong, props)
          : schemaType === SCHEMA_TYPE_BOOLEAN
            ? renderFieldComponent(CustomFieldBoolean, props)
            : schemaType === SCHEMA_TYPE_YOUTUBE
              ? renderFieldComponent(CustomFieldYoutube, props)
              : schemaType === SCHEMA_TYPE_PRICE
                ? renderFieldComponent(CustomPricingField, props)
                : schemaType === SCHEMA_TYPE_REPEATER
                  ? renderFieldComponent(CustomFieldRepeater, props)
                  : schemaType === SCHEMA_TYPE_OPEN_AND_CLOSE_TIME
                    ? renderFieldComponent(CustomFieldOpenAndCloseTime, props)
                : null;
};

export default CustomExtendedDataField;
